import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { enqueueSnackbar } from "notistack"
import DashboardLayout from "../../components/DashboardLayout"
import { usePermissions } from "../../hooks/usePermissions"
import { useAuth } from "../../contexts/AuthContext"
import { api } from "../../services/api"
import { MilitarGrupo } from "../../types/grupos"
import { PracasSelectField } from "../../components/PracasSelectField"
import { votacaoDestaqueService, VotacaoAtualResponse, RankingItem, OficialVotou } from "../../services/votacaoDestaque"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { Badge } from "../../components/ui/badge"
import { Button } from "../../components/ui/button"
import {
  Users,
  FileText,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Activity,
  Target,
  UserPlus,
  Star,
  Minus,
  Zap,
  LayoutDashboard,
  ArrowRight,
  MessageSquare,
  Trophy,
  CheckCircle2,
  Lock,
  Unlock,
  Medal,
  ShieldCheck,
  ClipboardList,
  ExternalLink,
  ChevronDown,
  ChevronUp,
} from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog"
import { PainelPracaModal } from "../../components/modals/PainelModal"
import { ForumPostCard } from "../Forum/components/ForumPostCard"
import { ForumPost } from "../../types/forum"
import { listForumPosts } from "../../services/forumService"
import { ForumNovaPublicacao } from "./ForumNovaPublicacao"
import {
  Area,
  AreaChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts"
import { dashboardService, DashboardStats, AlistadoMensal, DesempenhoPraca } from "../../services/dashboard"
import { DashboardSimples } from "./DashboardSimples"

interface GrupoComAcoesPendentes {
  id: string
  nome: string
  img: string
  cargo: string
  isAdmin: boolean
  isResponsavel: boolean
  acoesPendentes: number
  idTipo: string
  ordemTipo: number
  ordemGrupo: number
}

// Meta diária de alistados — ajuste conforme necessário
const META_ALISTADOS_DIA = 10

interface BigNumberCardProps {
  title: string
  value: number | string
  previousLabel?: string
  previousValue?: number | string
  icon: React.ElementType
  iconColor?: string
  iconBg?: string
  accentColor?: string
  extra?: React.ReactNode
  loading?: boolean
  goalMet?: boolean
}

function BigNumberCard({
  title,
  value,
  previousLabel,
  previousValue,
  icon: Icon,
  iconColor = "text-primary",
  iconBg = "bg-primary/10",
  accentColor = "border-l-primary",
  extra,
  loading,
  goalMet,
}: BigNumberCardProps) {
  const effectiveBorder = goalMet ? "border-l-green-500" : accentColor
  const effectiveIconBg  = goalMet ? "bg-green-500/10" : iconBg
  const effectiveIconClr = goalMet ? "text-green-500" : iconColor

  return (
    <Card className={`hover:shadow-md transition-all duration-200 border-border/50 border-l-4 ${effectiveBorder} ${goalMet ? "shadow-green-500/10 shadow-md" : ""}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 px-4 pt-3 pb-1">
        <CardTitle className="text-xs font-medium text-muted-foreground leading-tight">{title}</CardTitle>
        <div className={`p-1.5 ${effectiveIconBg} rounded-md shrink-0`}>
          {goalMet ? (
            <Trophy className={`w-3.5 h-3.5 ${effectiveIconClr} animate-bounce`} />
          ) : (
            <Icon className={`w-3.5 h-3.5 ${effectiveIconClr}`} />
          )}
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-0.5">
        {loading ? (
          <div className="h-7 w-20 bg-muted rounded animate-pulse" />
        ) : (
          <div className="relative group cursor-default">
            <div className={`text-2xl font-bold leading-tight flex items-center gap-1 ${
              goalMet ? "text-green-500 animate-pop-in animate-glow-green" : "text-foreground"
            }`}>
              <span>{typeof value === 'number' ? value.toLocaleString('pt-BR') : value}</span>
              {goalMet && <span className="text-base animate-bounce select-none">🎯</span>}
            </div>
            {goalMet && (
              <div className="pointer-events-none absolute bottom-full left-0 mb-2 z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <div className="bg-green-600 text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap shadow-lg font-semibold">
                  🎉 META BATIDA! AVANTE OFICIAIS!
                </div>
                <div className="absolute top-full left-3 w-2 h-2 bg-green-600 rotate-45 -mt-1" />
              </div>
            )}
          </div>
        )}
        {previousLabel !== undefined && previousValue !== undefined && (
          loading ? (
            <div className="h-3 w-24 bg-muted rounded animate-pulse" />
          ) : (
            <p className="text-[11px] text-muted-foreground">
              {previousLabel}:{" "}
              <span className="font-semibold text-foreground">{typeof previousValue === 'number' ? previousValue.toLocaleString('pt-BR') : previousValue}</span>
            </p>
          )
        )}
        {extra && <div className="pt-0.5">{extra}</div>}
      </CardContent>
    </Card>
  )
}

// ---------- Helpers ----------

function formatDiff(current: number, previous: number) {
  const diff = current - previous
  if (diff > 0)
    return (
      <span className="inline-flex items-center gap-0.5 text-green-500">
        <TrendingUp className="w-3 h-3" />+{diff}
      </span>
    )
  if (diff < 0)
    return (
      <span className="inline-flex items-center gap-0.5 text-red-500">
        <TrendingDown className="w-3 h-3" />{diff}
      </span>
    )
  return (
    <span className="inline-flex items-center gap-0.5 text-muted-foreground">
      <Minus className="w-3 h-3" />0
    </span>
  )
}

// ---------- Dashboard ----------

export function Dashboard() {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const { militar } = useAuth()

  if (!hasPermission("visualizar_dashboard")) {
    return <DashboardSimples />
  }
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [alistadosMensais, setAlistadosMensais] = useState<AlistadoMensal[]>([])
  const [loadingMensal, setLoadingMensal] = useState(true)
  const [isPainelModalOpen, setIsPainelModalOpen] = useState(false)
  const [forumPosts, setForumPosts] = useState<ForumPost[]>([])
  const [loadingForum, setLoadingForum] = useState(true)

  const [votacao, setVotacao] = useState<VotacaoAtualResponse | null>(null)
  const [loadingVotacao, setLoadingVotacao] = useState(true)
  const [votacaoErro, setVotacaoErro] = useState<string | null>(null)
  const [votandoId, setVotandoId] = useState<string | null>(null)
  const [votandoNick, setVotandoNick] = useState("")
  const [militarVotadoId, setMilitarVotadoId] = useState("")
  const [militarVotadoNick, setMilitarVotadoNick] = useState("")
  const [ranking, setRanking] = useState<RankingItem[]>([])
  const [confirmVotacao, setConfirmVotacao] = useState<{ acao: 'abrir' | 'fechar'; id?: string } | null>(null)
  const [executandoConfirm, setExecutandoConfirm] = useState(false)
  const [oficiaisVotaram, setOficiaisVotaram] = useState<OficialVotou[]>([])
  const [showVotaramModal, setShowVotaramModal] = useState(false)
  const [loadingVotaram, setLoadingVotaram] = useState(false)

  const refreshVotacao = () => {
    setLoadingVotacao(true)
    votacaoDestaqueService.getAtual()
      .then(setVotacao)
      .catch(console.error)
      .finally(() => setLoadingVotacao(false))
    votacaoDestaqueService.getRanking()
      .then(setRanking)
      .catch(() => setRanking([]))
  }

  const handleAbrirVotacao = async () => {
    setExecutandoConfirm(true)
    try {
      await votacaoDestaqueService.abrir()
      refreshVotacao()
      setConfirmVotacao(null)
    } catch (err: any) {
      setVotacaoErro(err?.response?.data?.message || "Erro ao abrir votação.")
    } finally {
      setExecutandoConfirm(false)
    }
  }

  const handleFecharVotacao = async (id: string) => {
    setExecutandoConfirm(true)
    try {
      const resultado = await votacaoDestaqueService.fechar(id)
      refreshVotacao()
      setConfirmVotacao(null)
      if (hasPermission('ver_nao_votaram_destaque') && resultado.oficiais_votaram.length > 0) {
        setOficiaisVotaram(resultado.oficiais_votaram)
        setShowVotaramModal(true)
      }
    } catch (err: any) {
      setVotacaoErro(err?.response?.data?.message || "Erro ao fechar votação.")
    } finally {
      setExecutandoConfirm(false)
    }
  }

  const handleVotar = async () => {
    if (!votacao?.votacao || !militarVotadoId) return
    setVotandoId(militarVotadoId)
    setVotacaoErro(null)
    try {
      await votacaoDestaqueService.votar(votacao.votacao.id, militarVotadoId)
      setMilitarVotadoId("")
      setMilitarVotadoNick("")
      refreshVotacao()
    } catch (err: any) {
      setVotacaoErro(err?.response?.data?.message || "Erro ao votar.")
    } finally {
      setVotandoId(null)
    }
  }

  const [gruposAdminResp, setGruposAdminResp] = useState<GrupoComAcoesPendentes[]>([])
  const [loadingGruposAdminResp, setLoadingGruposAdminResp] = useState(true)
  const [gruposExpanded, setGruposExpanded] = useState(true)
  const [treinamentosExpanded, setTreinamentosExpanded] = useState(false)

  const [desempenhoNick, setDesempenhoNick] = useState("")
  const [desempenho, setDesempenho] = useState<DesempenhoPraca | null>(null)
  const [loadingDesempenho, setLoadingDesempenho] = useState(false)
  const [desempenhoErro, setDesempenhoErro] = useState<string | null>(null)
  const [enviandoAnalise, setEnviandoAnalise] = useState(false)

  const handleDesempenhoSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!desempenhoNick.trim()) return
    setLoadingDesempenho(true)
    setDesempenhoErro(null)
    setDesempenho(null)
    try {
      const data = await dashboardService.getDesempenhoPraca(desempenhoNick.trim())
      setDesempenho(data)
    } catch (err: any) {
      setDesempenhoErro(err?.response?.data?.message || "Erro ao buscar desempenho.")
    } finally {
      setLoadingDesempenho(false)
    }
  }

  const handleEnviarAnalise = async () => {
    if (!desempenho) return
    setEnviandoAnalise(true)
    try {
      await dashboardService.enviarAnalisePromocao(desempenho.nick)
      enqueueSnackbar("Análise enviada para o Discord com sucesso!", { variant: "success" })
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || "Erro ao enviar análise.", { variant: "error" })
    } finally {
      setEnviandoAnalise(false)
    }
  }

  useEffect(() => {
    if (!militar?.id) {
      setLoadingGruposAdminResp(false)
      return
    }
    const fetchGruposComAcoes = async () => {
      try {
        const resp = await api.get(`/militares-grupos/meus-grupos`)
        const memberships: MilitarGrupo[] = Array.isArray(resp.data) ? resp.data : []

        const adminRespGroups = memberships.filter(
          (m) => m.grupoCargo?.admin === true || m.grupoCargo?.responsavel === true
        )

        const gruposComAcoes = await Promise.all(
          adminRespGroups.map(async (m) => {
            try {
              const acoesResp = await api.get(`/grupos/${m.grupo.id}/acoes`, {
                params: { status: 'pendente' },
              })
              const acoesPendentes =
                acoesResp.data?.total ??
                (Array.isArray(acoesResp.data?.data) ? acoesResp.data.data.length : 0)
              return {
                id: m.grupo.id,
                nome: m.grupo.grupo,
                img: m.grupo.img ?? '',
                cargo: m.grupoCargo.cargo,
                isAdmin: m.grupoCargo.admin,
                isResponsavel: m.grupoCargo.responsavel,
                acoesPendentes,
                idTipo: m.grupo.id_tipo ?? '',
                ordemTipo: m.grupo.tipo?.ordem ?? 0,
                ordemGrupo: m.grupo.ordem ?? 0,
              } as GrupoComAcoesPendentes
            } catch {
              return {
                id: m.grupo.id,
                nome: m.grupo.grupo,
                img: m.grupo.img ?? '',
                cargo: m.grupoCargo.cargo,
                isAdmin: m.grupoCargo.admin,
                isResponsavel: m.grupoCargo.responsavel,
                acoesPendentes: 0,
                idTipo: m.grupo.id_tipo ?? '',
                ordemTipo: m.grupo.tipo?.ordem ?? 0,
                ordemGrupo: m.grupo.ordem ?? 0,
              } as GrupoComAcoesPendentes
            }
          })
        )

        const sorted = gruposComAcoes.sort((a, b) => {
          if (a.ordemTipo !== b.ordemTipo) return b.ordemTipo - a.ordemTipo
          if (a.idTipo !== b.idTipo) return b.idTipo.localeCompare(a.idTipo)
          if (a.ordemGrupo !== b.ordemGrupo) return a.ordemGrupo - b.ordemGrupo
          return a.nome.localeCompare(b.nome, 'pt-BR')
        })

        setGruposAdminResp(sorted)
      } catch (err) {
        console.error('[Dashboard] Erro ao carregar grupos admin/responsável:', err)
        setGruposAdminResp([])
      } finally {
        setLoadingGruposAdminResp(false)
      }
    }
    fetchGruposComAcoes()
  }, [militar?.id])

  useEffect(() => {
    dashboardService
      .getStats()
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false))

    dashboardService
      .getAlistadosMensais()
      .then(setAlistadosMensais)
      .catch(console.error)
      .finally(() => setLoadingMensal(false))

    listForumPosts({ orderBy: 'recentes', page: 1, limit: 10 })
      .then((res) => setForumPosts(res.posts))
      .catch(console.error)
      .finally(() => setLoadingForum(false))

    votacaoDestaqueService.getAtual()
      .then(setVotacao)
      .catch(console.error)
      .finally(() => setLoadingVotacao(false))

    votacaoDestaqueService.getRanking()
      .then(setRanking)
      .catch(() => setRanking([]))
  }, [])



  return (
    <DashboardLayout>
      <div className="p-6 space-y-8">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-primary to-primary/80 rounded-xl p-8 text-primary-foreground">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-primary-foreground/70 text-sm font-medium mb-1">
                Bem-vindo de volta
              </p>
              <h1 className="text-3xl font-bold">
                {militar?.nick ?? "Militar"}
              </h1>
            </div>
            <div className="hidden md:block">
              <div className="w-16 h-16 bg-primary-foreground/20 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-8 h-8" />
              </div>
            </div>
          </div>
        </div>

        {/* BigNumbers Grid */}
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-4">Estatísticas</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 xl:grid-cols-8 gap-3">

            {/* Total Alistados */}
            <BigNumberCard
              title="Total Alistados"
              value={stats?.totalAlistados ? stats.totalAlistados + 15000 : "—"}
              icon={Users}
              iconColor="text-violet-500"
              iconBg="bg-violet-500/10"
              accentColor="border-l-violet-500"
              loading={loading}
            />

            {/* Alistados (Ano) */}
            <BigNumberCard
              title="Alistados (Ano)"
              value={stats?.alistadosAno ?? "—"}
              previousLabel="Ano passado"
              previousValue={stats?.alistadosAnoPassado ?? "—"}
              icon={Activity}
              iconColor="text-sky-500"
              iconBg="bg-sky-500/10"
              accentColor="border-l-sky-500"
              loading={loading}
              extra={
                stats && (
                  <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                    vs ano passado: {formatDiff(stats.alistadosAno, stats.alistadosAnoPassado)}
                  </div>
                )
              }
            />

            {/* Alistados (Semana) */}
            <BigNumberCard
              title="Alistados (Semana)"
              value={stats?.alistadosSemana ?? "—"}
              previousLabel="Semana passada"
              previousValue={stats?.alistadosSemanaPassada ?? "—"}
              icon={UserPlus}
              iconColor="text-emerald-500"
              iconBg="bg-emerald-500/10"
              accentColor="border-l-emerald-500"
              loading={loading}
              extra={
                stats && (
                  <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                    vs semana passada: {formatDiff(stats.alistadosSemana, stats.alistadosSemanaPassada)}
                  </div>
                )
              }
            />

            {/* Alistados (Dia) */}
            <BigNumberCard
              title="Alistados (Dia)"
              value={stats?.alistadosDia ?? "—"}
              previousLabel="Ontem"
              previousValue={stats?.alistadosOntem ?? "—"}
              icon={UserPlus}
              iconColor="text-teal-500"
              iconBg="bg-teal-500/10"
              accentColor="border-l-teal-500"
              loading={loading}
              goalMet={!loading && typeof stats?.alistadosDia === "number" && stats.alistadosDia >= META_ALISTADOS_DIA}
              extra={
                stats && (
                  <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                    vs ontem: {formatDiff(stats.alistadosDia, stats.alistadosOntem)}
                  </div>
                )
              }
            />

            {/* Promoções (Dia) */}
            <BigNumberCard
              title="Promoções (Dia)"
              value={stats?.promocoesDia ?? "—"}
              previousLabel="Ontem"
              previousValue={stats?.promocoesOntem ?? "—"}
              icon={Star}
              iconColor="text-amber-500"
              iconBg="bg-amber-500/10"
              accentColor="border-l-amber-500"
              loading={loading}
              extra={
                stats && (
                  <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                    vs ontem: {formatDiff(stats.promocoesDia, stats.promocoesOntem)}
                  </div>
                )
              }
            />

            {/* Treinamentos (Dia) */}
            <BigNumberCard
              title="Treinamentos (Dia)"
              value={stats?.treinamentosDia ?? "—"}
              previousLabel="Ontem"
              previousValue={stats?.treinamentosOntem ?? "—"}
              icon={Target}
              iconColor="text-blue-500"
              iconBg="bg-blue-500/10"
              accentColor="border-l-blue-500"
              loading={loading}
              extra={
                stats && (
                  <div className="space-y-1">
                    <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                      vs ontem: {formatDiff(stats.treinamentosDia, stats.treinamentosOntem)}
                    </div>
                    {stats.treinamentosPorTipo.length > 0 && (
                      <>
                        <button
                          type="button"
                          onClick={() => setTreinamentosExpanded((v) => !v)}
                          className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {treinamentosExpanded
                            ? <ChevronUp className="w-3 h-3" />
                            : <ChevronDown className="w-3 h-3" />}
                          {treinamentosExpanded ? "Ocultar detalhe" : "Ver por tipo"}
                        </button>
                        {treinamentosExpanded && (
                          <div className="flex flex-col gap-0.5 pt-0.5 border-t border-border/40">
                            {stats.treinamentosPorTipo.map((t) => (
                              <div
                                key={t.nome}
                                className="flex items-center justify-between text-[11px] text-muted-foreground"
                              >
                                <span className="truncate max-w-[110px]">{t.nome}</span>
                                <Badge variant="secondary" className="text-[10px] px-1 py-0 ml-1 shrink-0">
                                  {t.quantidade}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )
              }
            />

            {/* Patrulhas (Dia) */}
            <BigNumberCard
              title="Patrulhas (Dia)"
              value={stats?.patrulhasDia ?? "—"}
              previousLabel="Ontem"
              previousValue={stats?.patrulhasOntem ?? "—"}
              icon={ShieldCheck}
              iconColor="text-cyan-500"
              iconBg="bg-cyan-500/10"
              accentColor="border-l-cyan-500"
              loading={loading}
              extra={
                stats && (
                  <div className="space-y-0.5">
                    <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                      vs ontem: {formatDiff(stats.patrulhasDia, stats.patrulhasOntem)}
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Concluídas:{" "}
                      <span className="font-semibold text-foreground">{stats.patrulhasConcluidasDia.toLocaleString('pt-BR')}</span>
                    </p>
                  </div>
                )
              }
            />

            {/* Ações Grupos (Dia) */}
            <BigNumberCard
              title="Ações Grupos (Dia)"
              value={stats?.acoesDia ?? "—"}
              previousLabel="Ontem"
              previousValue={stats?.acoesOntem ?? "—"}
              icon={ClipboardList}
              iconColor="text-orange-500"
              iconBg="bg-orange-500/10"
              accentColor="border-l-orange-500"
              loading={loading}
              extra={
                stats && (
                  <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                    vs ontem: {formatDiff(stats.acoesDia, stats.acoesOntem)}
                  </div>
                )
              }
            />
          </div>
        </div>

        {/* Meus Grupos — Admin / Responsável */}
        {(loadingGruposAdminResp || gruposAdminResp.length > 0) && (
          <div>
            <button
              onClick={() => setGruposExpanded((v) => !v)}
              className="flex items-center gap-2 mb-4 group w-full text-left"
            >
              <h2 className="text-xl font-semibold text-foreground">Meus Grupos</h2>
              {!loadingGruposAdminResp && (
                <span className="text-[11px] text-muted-foreground font-normal">
                  ({gruposAdminResp.length} grupo{gruposAdminResp.length !== 1 ? 's' : ''})
                </span>
              )}
              <span className="ml-1 text-muted-foreground group-hover:text-foreground transition-colors">
                {gruposExpanded
                  ? <ChevronUp className="w-4 h-4" />
                  : <ChevronDown className="w-4 h-4" />}
              </span>
            </button>
            {gruposExpanded && (loadingGruposAdminResp ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-28 bg-muted rounded-xl animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                {gruposAdminResp.map((grupo) => (
                  <button
                    key={grupo.id}
                    onClick={() => window.open(`/grupos/${grupo.nome.toLowerCase().replace(/°/g, '_')}`, '_blank')}
                    className="group relative text-left rounded-xl border border-border/50 bg-card hover:border-primary/40 hover:shadow-md transition-all duration-200 p-4 flex flex-col gap-3"
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {grupo.img ? (
                          <img
                            src={grupo.img}
                            alt={grupo.nome}
                            className="w-7 h-7 rounded-md object-cover shrink-0"
                          />
                        ) : (
                          <div className="p-1.5 bg-primary/10 rounded-md shrink-0">
                            <ShieldCheck className="w-4 h-4 text-primary" />
                          </div>
                        )}
                        <p className="font-semibold text-sm text-foreground truncate">{grupo.nome}</p>
                      </div>
                      <ExternalLink className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors shrink-0 mt-0.5" />
                    </div>

                    {/* Badges de cargo */}
                    <div className="flex flex-wrap gap-1">
                      {grupo.isAdmin && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-500/10 text-violet-600 dark:text-violet-400">
                          Admin
                        </span>
                      )}
                      {grupo.isResponsavel && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-sky-500/10 text-sky-600 dark:text-sky-400">
                          Responsável
                        </span>
                      )}
                      <span
                        className="text-[10px] text-muted-foreground px-1.5 py-0.5 rounded-full bg-muted truncate max-w-[140px]"
                        title={grupo.cargo}
                      >
                        {grupo.cargo}
                      </span>
                    </div>

                    {/* Ações pendentes */}
                    <div className="flex items-center gap-2 mt-auto pt-1 border-t border-border/30">
                      <ClipboardList className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <span className="text-xs text-muted-foreground flex-1">Ações pendentes</span>
                      <span
                        className={`text-sm font-bold ${
                          grupo.acoesPendentes > 0 ? 'text-amber-500' : 'text-muted-foreground'
                        }`}
                      >
                        {grupo.acoesPendentes}
                      </span>
                      {grupo.acoesPendentes > 0 && (
                        <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Visão Mensal + Ações Rápidas */}
        <div className="flex flex-col gap-6">

          {/* Row 1: Gráfico + Ações Rápidas */}
          <div className="flex flex-col lg:flex-row gap-6">

          {/* Visão Mensal de Alistados */}
          <Card className="border-border/50 flex-1 min-w-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-xl text-foreground flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Visão Mensal de Alistados
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingMensal ? (
                <div className="h-52 w-full bg-muted rounded animate-pulse" />
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={alistadosMensais} margin={{ top: 24, right: 16, left: -16, bottom: 0 }}>
                    <defs>
                      <linearGradient id="greenGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="4 4" stroke="currentColor" strokeOpacity={0.06} />
                    <XAxis
                      dataKey="mes"
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload?.length) {
                          const d = payload[0]?.payload
                          return (
                            <div className="bg-card border border-border rounded-lg px-3 py-2 text-sm shadow-md">
                              <p className="font-medium text-foreground">
                                {d?.mes}{d?.ano ? ` ${d.ano}` : ''}
                              </p>
                              <p className="text-muted-foreground">
                                Alistados: <span className="font-bold text-emerald-500">{Number(payload[0]?.value).toLocaleString('pt-BR')}</span>
                              </p>
                            </div>
                          )
                        }
                        return null
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="quantidade"
                      stroke="#22c55e"
                      strokeWidth={2.5}
                      fill="url(#greenGradient)"
                      dot={(props: any) => {
                        const { cx, cy, value } = props
                        const num = Array.isArray(value) ? value[1] : Number(value)
                        return (
                          <g key={`dot-${cx}-${cy}`}>
                            <circle cx={cx} cy={cy} r={4} fill="#22c55e" stroke="#fff" strokeWidth={2} />
                            <text
                              x={cx}
                              y={cy - 10}
                              textAnchor="middle"
                              fontSize={10}
                              fontWeight={600}
                              fill="#22c55e"
                            >
                              {num}
                            </text>
                          </g>
                        )
                      }}
                      activeDot={{ r: 6, fill: "#22c55e", stroke: "#fff", strokeWidth: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Ações Rápidas */}
          <Card className="border-border/50 w-full lg:w-80 shrink-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-xl text-foreground flex items-center gap-2">
                <Zap className="w-5 h-5 text-primary" />
                Ações Rápidas
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2 pt-2">

              {/* Gestão de Militares */}
              <button
                onClick={() => navigate("/registros/gestao-militares")}
                className="group flex items-center gap-3 w-full rounded-lg px-3 py-3 text-left transition-all hover:bg-violet-500/10 border border-transparent hover:border-violet-500/20"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-500/10 group-hover:bg-violet-500/20 transition-colors">
                  <Users className="w-4 h-4 text-violet-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">Gestão de Militares</p>
                  <p className="text-[11px] text-muted-foreground truncate">Cadastrar e gerenciar efetivo</p>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-violet-500 group-hover:translate-x-0.5 transition-all shrink-0" />
              </button>

              {/* Criar Relatório */}
              <button
                onClick={() =>
                  window.open(
                    "relatorio-treinamento",
                    "janela1",
                    "width=550, height=700, top=50, left=350, status=no, toolbar=no, location=no, menubar=no, resizable=no, fullscreen=no"
                  )
                }
                className="group flex items-center gap-3 w-full rounded-lg px-3 py-3 text-left transition-all hover:bg-emerald-500/10 border border-transparent hover:border-emerald-500/20"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 group-hover:bg-emerald-500/20 transition-colors">
                  <FileText className="w-4 h-4 text-emerald-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">Criar Relatório</p>
                  <p className="text-[11px] text-muted-foreground truncate">Relatório de treinamento</p>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-emerald-500 group-hover:translate-x-0.5 transition-all shrink-0" />
              </button>

              {/* Criar Painel */}
              <button
                onClick={() => setIsPainelModalOpen(true)}
                className="group flex items-center gap-3 w-full rounded-lg px-3 py-3 text-left transition-all hover:bg-amber-500/10 border border-transparent hover:border-amber-500/20"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 group-hover:bg-amber-500/20 transition-colors">
                  <LayoutDashboard className="w-4 h-4 text-amber-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">Criar Painel</p>
                  <p className="text-[11px] text-muted-foreground truncate">Painel de praça</p>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-amber-500 group-hover:translate-x-0.5 transition-all shrink-0" />
              </button>

            </CardContent>
          </Card>

          </div>{/* fim Row 1 */}

          {/* Row 2: Fórum + Coluna direita */}
          <div className="flex flex-col-reverse lg:flex-row gap-6 items-start">

          {/* Últimos Posts do Fórum */}
          <div className="flex-1 min-w-0 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-primary" />
                Últimos Posts do Fórum
              </h2>
              <ForumNovaPublicacao
                onPostCreated={(post) => setForumPosts((prev) => [post, ...prev])}
              />
            </div>
            {loadingForum ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-24 w-full bg-muted rounded-lg animate-pulse" />
                ))}
              </div>
            ) : forumPosts.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground text-sm">Nenhum post no fórum ainda.</div>
            ) : (
              <div className="space-y-3">
                {forumPosts.map((post) => (
                  <ForumPostCard
                    key={post.id}
                    post={post}
                    onPostUpdated={(updated) =>
                      setForumPosts((prev) => prev.map((p) => p.id === updated.id ? updated : p))
                    }
                    onPostDeleted={(id) =>
                      setForumPosts((prev) => prev.filter((p) => p.id !== id))
                    }
                  />
                ))}
              </div>
            )}
          </div>

          {/* Coluna direita: Desempenho + Votação */}
          <div className="w-full lg:w-80 shrink-0 flex flex-col gap-6">

          {/* Desempenho Mensal e Semanal */}
          <Card className="border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xl text-foreground flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Desempenho
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-0">
              {/* Busca */}
              <form onSubmit={handleDesempenhoSearch} className="flex gap-1.5">
                <input
                  type="text"
                  value={desempenhoNick}
                  onChange={(e) => setDesempenhoNick(e.target.value)}
                  placeholder="Nick da praça..."
                  className="flex-1 px-2 py-1.5 text-xs rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <button
                  type="submit"
                  disabled={loadingDesempenho}
                  className="px-2.5 py-1.5 text-xs rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors shrink-0"
                >
                  {loadingDesempenho ? "..." : "Buscar"}
                </button>
              </form>

              {/* Erro */}
              {desempenhoErro && (
                <p className="text-[11px] text-red-500 bg-red-500/10 rounded px-2 py-1">{desempenhoErro}</p>
              )}

              {/* Skeleton */}
              {loadingDesempenho && (
                <div className="space-y-1.5">
                  <div className="h-10 w-full bg-muted rounded animate-pulse" />
                  <div className="h-24 w-full bg-muted rounded animate-pulse" />
                </div>
              )}

              {/* Resultado */}
              {desempenho && !loadingDesempenho && (
                <div className="space-y-2">
                  {/* Avatar + Nick */}
                  <div className="flex items-center gap-2 pb-1 border-b border-border/40">
                    <img
                      src={`https://www.habbo.com.br/habbo-imaging/avatarimage?user=${encodeURIComponent(desempenho.nick)}&action=std&direction=4&head_direction=2&img_format=png&gesture=std&headonly=1&size=m`}
                      alt={desempenho.nick}
                      className="w-10 h-10 rounded"
                    />
                    <div>
                      <p className="text-sm font-bold text-foreground leading-tight">{desempenho.nick}</p>
                      <p className="text-[11px] text-muted-foreground">{desempenho.patente}</p>
                    </div>
                  </div>

                  {/* Status de prazo para promoção */}
                  {desempenho.diasDesdePromocao !== null && (
                    desempenho.aptoParaPromocao ? (
                      <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-green-500/10 border border-green-500/30">
                        <Star className="w-3.5 h-3.5 text-green-500 shrink-0" />
                        <p className="text-[11px] text-green-600 dark:text-green-400 font-semibold leading-tight">
                          Está no prazo para ser promovido ({desempenho.diasDesdePromocao}d na patente, prazo: {desempenho.prazoPatente}d)
                        </p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-red-500/10 border border-red-500/30">
                        <Star className="w-3.5 h-3.5 text-red-500 shrink-0" />
                        <p className="text-[11px] text-red-600 dark:text-red-400 font-semibold leading-tight">
                          Não está no prazo para ser promovido ({desempenho.diasDesdePromocao}d na patente{desempenho.prazoPatente !== null ? `, prazo: ${desempenho.prazoPatente}d` : ", sem prazo definido"})
                        </p>
                      </div>
                    )
                  )}

                  {/* Tabela compacta */}
                  <table className="w-full text-[11px] border-collapse">
                    <thead>
                      <tr>
                        <th className="text-left text-muted-foreground font-medium pb-1 pr-1 w-[45%]">Atividade</th>
                        <th className="text-center text-muted-foreground font-medium pb-1 px-0.5">SA</th>
                        <th className="text-center text-muted-foreground font-medium pb-1 px-0.5">SP</th>
                        <th className="text-center text-muted-foreground font-medium pb-1 px-0.5">M</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-border/30">
                        <td className="py-1 pr-1 text-foreground/80 font-medium">Patrulhas</td>
                        <td className="py-1 px-0.5 text-center font-bold text-foreground">{desempenho.patrulhasSA}</td>
                        <td className="py-1 px-0.5 text-center text-muted-foreground">{desempenho.patrulhasSP}</td>
                        <td className="py-1 px-0.5 text-center font-semibold text-sky-500">{desempenho.patrulhasMes}</td>
                      </tr>
                      <tr className="border-t border-border/30">
                        <td className="py-1 pr-1 text-foreground/80 font-medium">Treinamentos</td>
                        <td className="py-1 px-0.5 text-center font-bold text-foreground">{desempenho.treinamentosSA}</td>
                        <td className="py-1 px-0.5 text-center text-muted-foreground">{desempenho.treinamentosSP}</td>
                        <td className="py-1 px-0.5 text-center font-semibold text-emerald-500">{desempenho.treinamentosMes}</td>
                      </tr>
                      <tr className="border-t border-border/30">
                        <td className="py-1 pr-1 text-foreground/80 font-medium">Auxílios</td>
                        <td className="py-1 px-0.5 text-center font-bold text-foreground">{desempenho.auxiliosSA}</td>
                        <td className="py-1 px-0.5 text-center text-muted-foreground">{desempenho.auxiliosSP}</td>
                        <td className="py-1 px-0.5 text-center font-semibold text-violet-500">{desempenho.auxiliosMes}</td>
                      </tr>
                      {desempenho.grupos.length > 0 && (
                        <>
                          <tr>
                            <td colSpan={4} className="pt-2 pb-0.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                              Grupos Externos
                            </td>
                          </tr>
                          {desempenho.grupos.map((g) => (
                            <tr key={g.grupoId} className="border-t border-border/20">
                              <td
                                className="py-1 pr-1 text-foreground/70 truncate max-w-[80px]"
                                title={g.grupoNome}
                              >
                                {g.grupoNome}
                              </td>
                              <td className="py-1 px-0.5 text-center font-bold text-foreground">{g.acoesSA}</td>
                              <td className="py-1 px-0.5 text-center text-muted-foreground">{g.acoesSP}</td>
                              <td className="py-1 px-0.5 text-center font-semibold text-amber-500">{g.acoesMes}</td>
                            </tr>
                          ))}
                        </>
                      )}
                    </tbody>
                  </table>

                  {/* Legenda */}
                  <p className="text-[10px] text-muted-foreground border-t border-border/30 pt-1.5 leading-relaxed">
                    <span className="font-semibold text-foreground">SA</span> = Semana Atual&nbsp;&nbsp;·&nbsp;&nbsp;
                    <span className="font-semibold text-foreground">SP</span> = Sem. Anterior&nbsp;&nbsp;·&nbsp;&nbsp;
                    <span className="font-semibold text-foreground">M</span> = Mês Atual
                  </p>

                  {/* Botão enviar para análise */}
                  <button
                    type="button"
                    onClick={handleEnviarAnalise}
                    disabled={enviandoAnalise}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 text-xs font-semibold transition-colors border border-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {enviandoAnalise ? (
                      <>
                        <svg className="w-3.5 h-3.5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <svg className="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 2L11 13" /><path d="M22 2L15 22l-4-9-9-4 20-7z" />
                        </svg>
                        Enviar para análise de promoção
                      </>
                    )}
                  </button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Votação do Destaque */}
          <Card className="border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xl text-foreground flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                Votação do Destaque
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-0">
              {loadingVotacao ? (
                <div className="space-y-1.5">
                  <div className="h-6 w-full bg-muted rounded animate-pulse" />
                  <div className="h-16 w-full bg-muted rounded animate-pulse" />
                </div>
              ) : !votacao?.votacao ? (
                <div className="space-y-2">
                  <p className="text-[11px] text-muted-foreground text-center py-2">
                    Nenhuma votação ativa no momento.
                  </p>
                  {hasPermission("abrir_votacao_destaque") && (
                    <button
                      onClick={() => setConfirmVotacao({ acao: 'abrir' })}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 text-xs font-semibold transition-colors border border-amber-500/20"
                    >
                      <Unlock className="w-3.5 h-3.5" />
                      Abrir Votação
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Status badge */}
                  <div className="flex items-center justify-between">
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                      votacao.votacao.status === "aberta"
                        ? "bg-emerald-500/10 text-emerald-600"
                        : "bg-muted text-muted-foreground"
                    }`}>
                      {votacao.votacao.status === "aberta"
                        ? <><CheckCircle2 className="w-3 h-3" /> Aberta</>
                        : <><Lock className="w-3 h-3" /> Fechada</>
                      }
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(votacao.votacao.semana_referencia).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}
                    </span>
                  </div>

                  {/* Votação aberta: formulário */}
                  {votacao.votacao.status === "aberta" && !votacao.ja_votou && hasPermission("votar_destaque") && (
                    <div className="space-y-1.5">
                      <PracasSelectField
                        value={militarVotadoId}
                        onChange={(id, nick) => { setMilitarVotadoId(id); setMilitarVotadoNick(nick) }}
                        placeholder="Selecionar praça..."
                        label=""
                      />
                      <button
                        onClick={handleVotar}
                        disabled={!militarVotadoId || !!votandoId}
                        className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-xs font-semibold transition-colors"
                      >
                        <Medal className="w-3.5 h-3.5" />
                        {votandoId ? "Votando..." : "Votar"}
                      </button>
                    </div>
                  )}

                  {/* Já votou */}
                  {votacao.votacao.status === "aberta" && votacao.ja_votou && hasPermission("votar_destaque") && (
                    <p className="text-[11px] text-emerald-600 bg-emerald-500/10 rounded px-2 py-1 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Você já votou nesta semana!
                    </p>
                  )}

                  {/* Erro */}
                  {votacaoErro && (
                    <p className="text-[11px] text-red-500 bg-red-500/10 rounded px-2 py-1">{votacaoErro}</p>
                  )}

                  {/* Ranking */}
                  {ranking.length > 0 && (
                    <div className="space-y-0.5">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide pb-0.5">
                        Ranking — {votacao.total_votos} voto{votacao.total_votos !== 1 ? "s" : ""}
                      </p>
                      {ranking.slice(0, 5).map((r, i) => (
                        <div key={r.id_militar_votado} className="flex items-center gap-1.5 py-0.5">
                          <span className={`text-[10px] font-bold w-4 text-center ${i === 0 ? "text-amber-500" : i === 1 ? "text-slate-400" : i === 2 ? "text-amber-700" : "text-muted-foreground"}`}>
                            {i + 1}º
                          </span>
                          <img
                            src={`https://www.habbo.com.br/habbo-imaging/avatarimage?user=${encodeURIComponent(r.nick)}&action=std&direction=4&head_direction=2&img_format=png&gesture=std&headonly=1&size=s`}
                            alt={r.nick}
                            className="w-5 h-5 rounded"
                          />
                          <span className="flex-1 text-[11px] text-foreground truncate">{r.nick}</span>
                          <span className="text-[11px] font-bold text-amber-500">{r.votos}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Controles de permissão */}
                  <div className="pt-1 space-y-1">
                    {votacao.votacao.status === "aberta" && hasPermission("fechar_votacao_destaque") && (
                      <button
                        onClick={() => setConfirmVotacao({ acao: 'fechar', id: votacao.votacao!.id })}
                        className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-muted hover:bg-destructive/10 text-muted-foreground hover:text-destructive text-xs font-medium transition-colors border border-border/50"
                      >
                        <Lock className="w-3 h-3" />
                        Fechar Votação
                      </button>
                    )}
                    {votacao.votacao.status === "fechada" && hasPermission("ver_nao_votaram_destaque") && (
                      <button
                        onClick={async () => {
                          setLoadingVotaram(true)
                          try {
                            const lista = await votacaoDestaqueService.getVotaram(votacao.votacao!.id)
                            setOficiaisVotaram(lista)
                            setShowVotaramModal(true)
                          } catch (err: any) {
                            enqueueSnackbar(err?.response?.data?.message || "Erro ao buscar oficiais.", { variant: "error" })
                          } finally {
                            setLoadingVotaram(false)
                          }
                        }}
                        disabled={loadingVotaram}
                        className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-muted hover:bg-muted/80 text-muted-foreground text-xs font-medium transition-colors border border-border/50 disabled:opacity-50"
                      >
                        <ShieldCheck className="w-3 h-3" />
                        {loadingVotaram ? "Carregando..." : "Ver quem votou"}
                      </button>
                    )}
                    {votacao.votacao.status === "fechada" && hasPermission("abrir_votacao_destaque") && (
                      <button
                        onClick={() => setConfirmVotacao({ acao: 'abrir' })}
                        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 text-xs font-semibold transition-colors border border-amber-500/20"
                      >
                        <Unlock className="w-3.5 h-3.5" />
                        Abrir Nova Votação
                      </button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          </div>{/* fim coluna direita */}
          </div>{/* fim Row 2 */}
        </div>{/* fim flex-col externo */}
      </div>
      <PainelPracaModal
          isOpen={isPainelModalOpen}
          onClose={() => setIsPainelModalOpen(false)}
        />

      {/* Modal de confirmação: Abrir / Fechar Votação */}
      <Dialog open={!!confirmVotacao} onOpenChange={(open) => { if (!open) setConfirmVotacao(null) }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {confirmVotacao?.acao === 'abrir'
                ? <><Unlock className="w-4 h-4 text-amber-500" /> Abrir Votação do Destaque</>
                : <><Lock className="w-4 h-4 text-destructive" /> Fechar Votação do Destaque</>
              }
            </DialogTitle>
            <DialogDescription>
              {confirmVotacao?.acao === 'abrir'
                ? "Tem certeza que deseja abrir uma nova votação do destaque da semana? Os membros poderão começar a votar."
                : "Tem certeza que deseja fechar a votação atual? Os votos serão contabilizados e nenhum novo voto poderá ser registrado."
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <button
              onClick={() => setConfirmVotacao(null)}
              disabled={executandoConfirm}
              className="px-4 py-2 text-sm rounded-md border border-border bg-background hover:bg-muted text-foreground transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={() => {
                if (confirmVotacao?.acao === 'abrir') handleAbrirVotacao()
                else if (confirmVotacao?.acao === 'fechar' && confirmVotacao.id) handleFecharVotacao(confirmVotacao.id)
              }}
              disabled={executandoConfirm}
              className={`px-4 py-2 text-sm rounded-md font-semibold text-white transition-colors disabled:opacity-50 ${
                confirmVotacao?.acao === 'abrir'
                  ? "bg-amber-500 hover:bg-amber-600"
                  : "bg-destructive hover:bg-destructive/90"
              }`}
            >
              {executandoConfirm
                ? "Aguarde..."
                : confirmVotacao?.acao === 'abrir' ? "Sim, abrir" : "Sim, fechar"
              }
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Modal: Oficiais que votaram */}
      <Dialog open={showVotaramModal} onOpenChange={setShowVotaramModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-500" />
              Oficiais que votaram
            </DialogTitle>
            <DialogDescription>
              {oficiaisVotaram.length} oficial(is) ativo(s) participaram desta votação.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-64 overflow-y-auto space-y-1 py-1">
            {oficiaisVotaram.map((o) => (
              <div key={o.id} className="flex items-center justify-between px-3 py-2 rounded-md bg-muted/50 text-sm">
                <span className="font-medium text-foreground">{o.nick}</span>
                <span className="text-xs text-muted-foreground">{o.patente_abreviacao}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <button
              onClick={() => setShowVotaramModal(false)}
              className="px-4 py-2 text-sm rounded-md border border-border bg-background hover:bg-muted text-foreground transition-colors"
            >
              Fechar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  )
}
